import * as React from 'react'
import {useAsync} from 'react-async-hook'

import useStore from 'airship/hooks/use-store'

import errorContext, {emptyErrorState} from './error-context'
import {AsyncValidationHandler, ValidationHandler} from './types'
import {asyncValidate, validate} from './utils'

export interface FieldValidationProps<T> {
  validators: ValidationHandler<T>[]
  asyncValidators: AsyncValidationHandler<T>[]
  id: string
  value?: T
}

export const FieldValidation = <T,>({
  validators,
  asyncValidators,
  id,
  value,
  children,
}: FieldValidationProps<T> & {children?: React.ReactNode}): JSX.Element => {
  const {update} = React.useContext(errorContext)
  const {loading, error: runtimeError, result} = useAsync(
    () => asyncValidate(asyncValidators, value),
    [JSON.stringify(value)]
  )
  const [syncErrors, syncWarnings] = React.useMemo(() => {
    return validate(validators, value)
    // Many validators are defined inline and will change with every render, so we don't want them in this dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])
  const [asyncErrors, asyncWarnings] = React.useMemo(() => result ?? [[], []], [
    result,
  ])
  const isValid = React.useMemo(() => {
    if (asyncValidators.length > 0 && (loading || !result)) {
      return false
    }
    return syncErrors.length === 0 && asyncErrors.length === 0
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asyncErrors, loading, result, syncErrors])

  React.useEffect(() => {
    if (isValid) {
      update((s) => {
        s.errors[id] = []
        s.warnings[id] = [...syncWarnings, ...asyncWarnings]
        s.invalidFields = s.invalidFields.filter((f) => f !== id)
      })
      return
    }
    update((s) => {
      s.errors[id] = [...syncErrors, ...asyncErrors]
      s.warnings[id] = [...syncWarnings, ...asyncWarnings]
      s.invalidFields = Array.from(new Set([...s.invalidFields, id]))
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid, syncErrors, syncWarnings, asyncErrors])

  React.useEffect(() => {
    update((s) => (s.runtimeError = runtimeError))
  }, [runtimeError, update])

  // This teardown removes errors/warnings at the specific key
  // So they don't outlive the component and cause unexpected behavior
  React.useEffect(() => {
    return () =>
      update((s) => {
        delete s.errors[id]
        delete s.warnings[id]
        s.invalidFields = s.invalidFields.filter((f) => f !== id)
      })
  }, [id, update])

  React.useEffect(() => {
    update((s) => (s.invalidFields = [...s.invalidFields, id]))
    // This is intentional to set the initial invalid state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <>{children}</>
}

export interface ValidationScopeProps {
  onValid?: (valid: boolean) => void
  onError?: (error: Error) => void
}

export const ValidationScope: React.FC<ValidationScopeProps> = ({
  children,
  onValid = () => undefined,
  onError = () => undefined,
}) => {
  const {state, update} = useStore(emptyErrorState)
  const valid = React.useMemo(
    () => !state.invalidFields.length && !state.runtimeError,
    [state.invalidFields.length, state.runtimeError]
  )

  React.useEffect(() => {
    if (state.runtimeError) {
      onError(state.runtimeError)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.runtimeError])

  React.useEffect(() => {
    onValid(valid)
    update((s) => (s.valid = valid))
    // onValid callbacks are often declared inline and will cause loops if left in this dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valid])

  return (
    <errorContext.Provider
      value={{
        context: state,
        update: update,
      }}
    >
      {children}
    </errorContext.Provider>
  )
}
